import { Modal, Progress } from 'antd';
import React, { FC, memo, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { calculateInvoicePercents, Invoice, InvoiceItemType, useGetInvoiceByIdQuery } from 'entities/Invoice';
import { getUserName } from 'entities/User';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { RentalItemType } from 'entities/RentalItem';
import { PaymentProcessor } from 'entities/Payment';

interface InvoiceDetailsModalProps {
  invoiceId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface CalculatedInvoiceItemData {
  invoiceItemId: string;
  invoiceItemType: InvoiceItemType;
  contractItem: Nullable<Invoice['invoiceItems'][number]['contractItem']>;
  description?: string;
  totalPrice: number;
  paidAmount: number;
  creditedAmount: number;
  unpaidAmount: number;
  paymentDetails: Array<{
    paymentId: string;
    paymentDocumentNumber: string;
    paymentCreatedAt: string;
    amount: number;
    paymentMethod: PaymentProcessor;
  }>;
  creditNoteDetails: Array<{
    creditNoteId: string;
    creditNoteDocumentNumber: string;
    creditNoteCreatedAt: string;
    amount: number;
  }>;
}

export const InvoiceDetailsModal: FC<InvoiceDetailsModalProps> = memo((props) => {
  const { invoiceId, isOpen, onClose } = props;

  const { t } = useAppTranslation(['contracts', 'common']);
  const currencySymbol = useGetCurrencySymbol();

  const { data: invoice } = useGetInvoiceByIdQuery(invoiceId, {
    skip: !invoiceId || !isOpen,
  });

  const calculatedInvoiceItems = useMemo<CalculatedInvoiceItemData[]>(() => {
    const paidByInvoiceItemId: Record<string, number> = {};
    const paymentDetailsByInvoiceItemId: Record<string, CalculatedInvoiceItemData['paymentDetails']> = {};
    const creditNoteByCreditNoteItemId: Record<string, Invoice['creditNotes'][number]> = {};

    (invoice?.payments || []).forEach((payment) => {
      (payment.paymentItems || []).forEach((paymentItem) => {
        const invoiceItemId = paymentItem.invoiceItemId || paymentItem.invoiceItem?.invoiceItemId;

        if (!invoiceItemId) {
          return;
        }

        const cancelledBack = paymentItem.creditNoteItem?.creditedAmount || 0;
        const effectiveAmount = Math.max(0, roundNumber(paymentItem.amount - cancelledBack));

        if (!effectiveAmount) {
          return;
        }

        paidByInvoiceItemId[invoiceItemId] = roundNumber((paidByInvoiceItemId[invoiceItemId] || 0) + effectiveAmount);

        paymentDetailsByInvoiceItemId[invoiceItemId] = [
          ...(paymentDetailsByInvoiceItemId[invoiceItemId] || []),
          {
            paymentId: payment.paymentId,
            paymentDocumentNumber: payment.documentNumber,
            paymentCreatedAt: payment.createdAt,
            amount: effectiveAmount,
            paymentMethod: payment.paymentMethod,
          },
        ];
      });
    });

    (invoice?.creditNotes || []).forEach((creditNote) => {
      (creditNote.creditNoteItems || []).forEach((creditNoteItem) => {
        creditNoteByCreditNoteItemId[creditNoteItem.creditNoteItemId] = creditNote;
      });
    });

    return (invoice?.invoiceItems || []).map((invoiceItem) => {
      const creditedAmount = roundNumber(invoiceItem.creditNoteItems.reduce((acc, creditItem) => acc + creditItem.creditedAmount, 0));
      const paidAmount = roundNumber(paidByInvoiceItemId[invoiceItem.invoiceItemId] || 0);
      const unpaidAmount = Math.max(0, roundNumber(invoiceItem.totalPrice - creditedAmount - paidAmount));
      const creditNoteDetails = (invoiceItem.creditNoteItems || []).map((creditNoteItem) => {
        const parentCreditNote = creditNoteByCreditNoteItemId[creditNoteItem.creditNoteItemId];

        return {
          creditNoteId: parentCreditNote?.creditNoteId || creditNoteItem.creditNoteItemId,
          creditNoteDocumentNumber: parentCreditNote?.documentNumber || '-',
          creditNoteCreatedAt: parentCreditNote?.createdAt || creditNoteItem.createdAt,
          amount: roundNumber(creditNoteItem.creditedAmount),
        };
      });

      return {
        invoiceItemId: invoiceItem.invoiceItemId,
        invoiceItemType: invoiceItem.invoiceItemType,
        contractItem: invoiceItem.contractItem,
        description: invoiceItem.description,
        totalPrice: roundNumber(invoiceItem.totalPrice),
        paidAmount,
        creditedAmount,
        unpaidAmount,
        paymentDetails: paymentDetailsByInvoiceItemId[invoiceItem.invoiceItemId] || [],
        creditNoteDetails,
      };
    });
  }, [invoice?.creditNotes, invoice?.invoiceItems, invoice?.payments]);

  const totalPaidAmount = useMemo(
    () => roundNumber(calculatedInvoiceItems.reduce((acc, item) => acc + item.paidAmount, 0)),
    [calculatedInvoiceItems],
  );

  const totalCreditedAmount = useMemo(
    () => roundNumber(calculatedInvoiceItems.reduce((acc, item) => acc + item.creditedAmount, 0)),
    [calculatedInvoiceItems],
  );

  const baseInvoiceRows: TableRow[] = [
    { label: t('Invoice'), value: invoice?.documentNumber },
    { label: t('Client'), value: invoice?.user ? getUserName(invoice.user) : '-' },
    { label: t('Mail'), value: invoice?.invoiceEmail || '-', emptyValueSymbol: '-' },
    { label: t('Address'), value: invoice?.invoiceAddress || '-', emptyValueSymbol: '-' },
    {
      label: t('Date'),
      value: getClientDateFormat(invoice?.createdAt),
    },
    ...(invoice?.dateFrom && invoice?.dateTo
      ? [
          {
            label: t('Period'),
            value: `${getClientDateFormat(invoice.dateFrom)} - ${getClientDateFormat(invoice.dateTo)}`,
          },
        ]
      : []),
    ...(invoice?.dueDate
      ? [
          {
            label: t('Due date'),
            value: getClientDateFormat(invoice.dueDate),
          },
        ]
      : []),
    ...(invoice?.description
      ? [
          {
            label: t('Description'),
            value: (() => {
              const splitedDescription = splitDocumentDescription(invoice.description || '');
              return `${t(splitedDescription.description)} ${splitedDescription.documentNumber}`;
            })(),
            emptyValueSymbol: '-',
          },
        ]
      : []),
  ];

  const financialRows: TableRow[] = [
    { label: t('Total'), value: `${roundNumber(invoice?.amount)} ${currencySymbol}`, emptyValueSymbol: `0 ${currencySymbol}` },
    { label: t('Paid amount'), value: `${totalPaidAmount} ${currencySymbol}`, emptyValueSymbol: `0 ${currencySymbol}` },
    { label: t('Credited amount'), value: `${totalCreditedAmount} ${currencySymbol}`, emptyValueSymbol: `0 ${currencySymbol}` },
    {
      label: t('Debt'),
      value: `${roundNumber(invoice?.debt)} ${currencySymbol}`,
      emptyValueSymbol: `0 ${currencySymbol}`,
      highlighted: invoice?.debt ? invoice.debt > 0 : false,
    },
    { label: t('Payments count'), value: `${invoice?.payments.length}` },
    { label: t('Credit notes'), value: `${invoice?.creditNotes.length}` },
  ];

  return (
    <Modal
      title={`${t('Invoice')} - ${invoice?.documentNumber} ${t('Details')}`}
      open={isOpen}
      onCancel={onClose}
      width={1100}
      footer={null}
    >
      <div className="grid grid-cols-2 gap-6 my-8">
        <div className="border-r border-primary pr-6">
          <div className="mb-2 text-lg font-semibold">{t('Base info')}</div>
          <BulletsTable theme="clear" rows={baseInvoiceRows} />
          <div className="mt-6 mb-2 text-lg font-semibold">{t('Financial summary')}</div>
          <BulletsTable theme="clear" rows={financialRows} />
        </div>

        <div className="pl-6 max-h-[65vh] overflow-auto">
          <div className="mb-3 text-lg font-semibold">{t('Invoice items')}</div>

          {calculatedInvoiceItems.map((item) => {
            const isRentalItem = item.invoiceItemType === InvoiceItemType.RENTAL_ITEM;
            let itemType: RentalItemType | InvoiceItemType = item.invoiceItemType;

            if (isRentalItem && item.contractItem?.rentalItem?.rentalItemType) {
              itemType = item.contractItem?.rentalItem?.rentalItemType;
            }

            // Keep chart sane when there is overpayment: table shows real paid value, chart caps paid to non-credited remainder.
            const paidForChart = Math.min(item.paidAmount, Math.max(0, roundNumber(item.totalPrice - item.creditedAmount)));
            const percents = calculateInvoicePercents(item.totalPrice, paidForChart, item.creditedAmount);
            const unpaidPercent = roundNumber(100 - percents.paid - percents.credited);

            const itemRows: TableRow[] = [
              {
                label: t('Invoiced'),
                value: `${item.totalPrice} ${currencySymbol}`,
                emptyValueSymbol: `0 ${currencySymbol}`,
              },
              {
                label: t('Paid'),
                value: `${item.paidAmount} ${currencySymbol}`,
                emptyValueSymbol: `0 ${currencySymbol}`,
              },
              {
                label: t('Credited'),
                value: `${item.creditedAmount} ${currencySymbol}`,
                emptyValueSymbol: `0 ${currencySymbol}`,
              },
              {
                label: t('Debt'),
                value: `${item.unpaidAmount} ${currencySymbol}`,
                emptyValueSymbol: `0 ${currencySymbol}`,
                highlighted: item.unpaidAmount > 0,
              },
            ];

            return (
              <div key={item.invoiceItemId} className="border border-secondary rounded-lg my-2 p-4">
                <div className="font-semibold text-xl flex">
                  {t(itemType)}:
                  <span className="font-normal text-xl px-1">
                    {item.totalPrice} {currencySymbol}
                  </span>
                </div>

                <div className="mb-3">
                  <Progress
                    size={['100%', 10]}
                    percent={percents.paid + unpaidPercent}
                    success={{ percent: percents.paid }}
                    strokeColor="var(--primaryLight)"
                    trailColor="var(--warn)"
                    showInfo={false}
                  />
                </div>

                <div className="flex flex-row items-start gap-4">
                  <div className="w-[50%]">
                    <BulletsTable theme="clear" rows={itemRows} />
                  </div>
                  <div className="w-[50%]">
                    <div className="font-medium mb-1">{t('Payments')}</div>
                    {item.paymentDetails.length > 0 ? (
                      item.paymentDetails.map((paymentDetail) => (
                        <div key={`${item.invoiceItemId}-${paymentDetail.paymentId}`} className="text-sm mb-1">
                          {t(paymentDetail.paymentMethod, { ns: 'common' })} ({paymentDetail.paymentDocumentNumber}) <br />
                          {getClientDateFormat(paymentDetail.paymentCreatedAt)} - {roundNumber(paymentDetail.amount)} {currencySymbol}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-primaryLight">{t('No payments yet')}</div>
                    )}

                    <div className="font-medium mt-3 mb-1">{t('Credit notes')}</div>
                    {item.creditNoteDetails.length > 0 ? (
                      item.creditNoteDetails.map((creditNoteDetail) => (
                        <div key={`${item.invoiceItemId}-${creditNoteDetail.creditNoteId}`} className="text-sm mb-1">
                          {creditNoteDetail.creditNoteDocumentNumber} - {getClientDateFormat(creditNoteDetail.creditNoteCreatedAt)} -{' '}
                          {roundNumber(creditNoteDetail.amount)} {currencySymbol}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-primaryLight">{t('No credit notes yet')}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
});
